import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { INTERNAL_TRANSFER_IT_TRUST_TOGGLE } from '@shared/helpers';
import { AccountTypes, GiftingDesignationTypes } from '@shared/models';
import { LoggerCodes } from '@shared/modules/logger/logger';
import { GatekeeperService } from '@shared/services';
import { WindowRef } from '@shared/services/window-ref/window-ref.service';
import { getConfigClientFromRTE } from '@src/app/core/config/config-client';
import {
  getInternalTransferTriage,
  InternalTransferState,
  InternalTransferTriage,
} from '@store/reducers/internal-transfer/internal-transfer.reducer';
import { selectToFromConsolidationToggle } from '@store/selectors';
import { catchError, concatMap, mergeMap, withLatestFrom } from 'rxjs';

import { External, Go, InternalTransferSummaryContinue, LogError } from '../../actions';
import { State } from '../../reducers/index';
import { selectInternalTransferState } from '../../selectors/internal-transfer/internal-transfer.selectors';

@Injectable()
export class InternalTransferSummaryEffects {
  constructor(
    private actions$: Actions,
    private globalStore$: Store<State>,
    private windowRef: WindowRef,
    private gatekeeper: GatekeeperService,
  ) {}

  internalTransferSummaryContinue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InternalTransferSummaryContinue),
      withLatestFrom(
        this.globalStore$.pipe(select(selectInternalTransferState)),
        this.globalStore$.pipe(select(selectToFromConsolidationToggle)),
      ),
      mergeMap(([, triageState, toFromConsolidationToggle]) =>
        this.gatekeeper.getGatekeeper(INTERNAL_TRANSFER_IT_TRUST_TOGGLE).pipe(
          concatMap((trustToggle) => {
            const url = this.buildUrl(triageState, trustToggle, toFromConsolidationToggle);
            const actions = [];
            actions.push(External({ url: url }));
            return actions;
          }),
          catchError(() => {
            return [
              Go({ path: ['error'] }),
              LogError({
                payload: {
                  eventId: 'it-summary-effect-gateKeeper-error',
                  message: 'Failed to retrieve gateKeeper IT trust toggle',
                  code: LoggerCodes.NULL_FAILURE,
                },
              }),
            ];
          }),
        ),
      ),
    ),
  );

  buildUrl(triageState: InternalTransferState, toggle: boolean, toFromToggle: boolean): string {
    const config = getConfigClientFromRTE();
    const _triageState = getInternalTransferTriage(triageState);
    const accountFromType = _triageState.transferFrom;
    const accountToType = _triageState.transferTo;
    const isOwner = _triageState.isOwner;
    const exceptionsApply = _triageState.exceptionsApply;
    const isTransferConditionsAffirmed = _triageState.isTransferConditionsAffirmed;
    const isDivorceTransfer = _triageState.isDivorceTransfer;
    const intelledoxURL = config.pretty_ecs_domain + config.internal_transfer_intelledox_endpoint;
    const possibleInheritance = triageState.triage.transferTo === AccountTypes.ESTATE;
    const divorceFlow =
      !isTransferConditionsAffirmed &&
      isDivorceTransfer &&
      triageState.triage.transferFrom === AccountTypes.IRA;
    const estateTransfer = triageState.triage.transferFrom === AccountTypes.ESTATE;
    const isMinorGift = !estateTransfer && triageState.triage.transferTo === AccountTypes.UGMA_UTMA;
    const isToFromToggleOn = toFromToggle;

    const validAccountTypes = (type: AccountTypes): boolean => {
      const validTypes = [
        AccountTypes.INDV,
        AccountTypes.TRUST,
        AccountTypes.JOINT,
        AccountTypes.ESTATE,
        AccountTypes.UGMA_UTMA,
        AccountTypes.IRA,
      ];
      return validTypes.includes(type);
    };
    if (
      (!toggle &&
        (accountFromType === AccountTypes.TRUST || accountToType === AccountTypes.TRUST)) ||
      !validAccountTypes(accountFromType) ||
      (!validAccountTypes(accountToType) && !isDivorceTransfer)
    ) {
      return intelledoxURL;
    }
    const baseHref = this.windowRef.nativeWindow.location.href
      .split('?')[0]
      .slice(0, -'/internal-transfer/summary'.length);
    if (divorceFlow) {
      const divorceURL = baseHref + config.divorce_document_upload_endpoint;
      return divorceURL;
    } else if (possibleInheritance) {
      const inheritanceURL = baseHref + config.inheritance_overview_endpoint;
      return inheritanceURL;
    } else if ((!isOwner && !exceptionsApply && !estateTransfer) || isMinorGift) {
      const accountFrom: AccountTypes = triageState.triage.transferFrom;
      const giftingURL =
        baseHref +
        config.gifting_triage_endpoint +
        '/' +
        GiftingDesignationTypes.VANGUARD.toLowerCase() +
        '/' +
        accountFrom.toLowerCase();
      return giftingURL;
    }
    const internalTransferEndpoint = isToFromToggleOn
      ? config.internal_transfer_details_endpoint
      : config.internal_transfer_from_endpoint;
    const cooURL = baseHref + internalTransferEndpoint;
    return cooURL;
  }
}

export const effects: any[] = [InternalTransferSummaryEffects];
